import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link2, Copy, RefreshCw, Trash2, ShieldAlert, ShieldCheck } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { calendarAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

/**
 * Phase 27.30.16 — Share link management for the Event Workspace.
 *
 * Reusable tokenized link that lets an agent share the event and
 * capture registrations without login. One token per event (reusable
 * forever), generated on demand. Revocable.
 *
 * Behaviour depends on `event.requires_npn_to_register`:
 *  • ON  → registrants must provide an NPN. Recognised NPNs join as
 *          agents; unrecognised NPNs are captured as Prospects.
 *  • OFF → anyone with just first + last name can register (public
 *          celebrations, parties, community events).
 */
export default function ShareLinkPanel({ event, canManage, onRefresh }) {
  const eventId = event?.event_id;
  const [state, setState] = useState({ token: null, url: null });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    // We don't have a GET endpoint — the state comes from the event
    // itself (`share_link_token`). When the manager clicks Generate we
    // POST which is idempotent (returns the existing token if any).
    if (event?.share_link_token && !event.share_link_revoked_at) {
      const portal = window.location.origin;
      setState({
        token: event.share_link_token,
        url: `${portal}/events/share/${event.share_link_token}`,
      });
    } else {
      setState({ token: null, url: null });
    }
  }, [event]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await calendarAPI.shareLinkCreate(eventId);
      const portal = window.location.origin;
      setState({
        token: r.data.token,
        url: `${portal}/events/share/${r.data.token}`,
      });
      toast.success(r.data.reused ? 'Link ready' : 'Share link generated');
      onRefresh?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not create link'));
    } finally {
      setLoading(false);
    }
  };

  const revoke = async () => {
    if (!window.confirm(
      'Revoke this share link? Anyone who still has the URL will no longer '
      + 'be able to register. You can generate a new link afterwards.',
    )) return;
    setLoading(true);
    try {
      await calendarAPI.shareLinkRevoke(eventId);
      toast.success('Share link revoked');
      setState({ token: null, url: null });
      onRefresh?.();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Could not revoke'));
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    if (!state.url) return;
    navigator.clipboard.writeText(state.url).then(
      () => toast.success('Link copied'),
      () => toast.error('Could not copy'),
    );
  };

  const requiresNpn = !!event?.requires_npn_to_register;

  return (
    <Card data-testid="share-link-panel">
      <CardContent className="p-5 space-y-3">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2">
            <Link2 className="h-4 w-4 text-slate-500" />
            <div>
              <div className="text-sm font-semibold text-slate-800">Share link</div>
              <div className="text-[11px] text-slate-500">
                Reusable link — one per event. No login required to register.
              </div>
            </div>
          </div>
          <Badge
            variant="outline"
            className={requiresNpn
              ? 'text-amber-800 bg-amber-50 border-amber-200'
              : 'text-emerald-800 bg-emerald-50 border-emerald-200'}
            data-testid="share-link-mode"
          >
            {requiresNpn
              ? (<><ShieldAlert className="h-3 w-3 mr-1" /> NPN required</>)
              : (<><ShieldCheck className="h-3 w-3 mr-1" /> Public — no NPN</>)}
          </Badge>
        </div>

        {state.url ? (
          <>
            <div className="flex gap-2">
              <Input
                readOnly
                value={state.url}
                className="text-xs font-mono"
                data-testid="share-link-url"
                onFocus={(e) => e.target.select()}
              />
              <Button size="sm" variant="outline" onClick={copy} data-testid="share-link-copy">
                <Copy className="h-3.5 w-3.5 mr-1" /> Copy
              </Button>
            </div>
            <div className="flex items-center justify-between pt-1">
              <p className="text-[11px] text-slate-500">
                {requiresNpn
                  ? 'Registrants must enter an NPN, name, email, phone. Recognised NPNs join as agents; unrecognised NPNs are added to Prospects for recruiting.'
                  : 'Registrants only need first + last name. No CRM record is created for guests.'}
              </p>
              {canManage && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={revoke}
                  disabled={loading}
                  className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                  data-testid="share-link-revoke"
                >
                  <Trash2 className="h-3.5 w-3.5 mr-1" /> Revoke
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500">
              No share link yet. Generate one and share it via any channel —
              text, WhatsApp, email, social — anyone with the URL can register.
            </p>
            {canManage && (
              <Button
                size="sm"
                onClick={generate}
                disabled={loading}
                data-testid="share-link-generate"
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
                Generate link
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
